import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useMemo } from "react";
import { getNumberLabel } from "../../../../../../utils/numberUtils";
import {
  calculateProgress,
  HierarchicalSpendItem,
  getColorByLevel,
} from "../utils";
import Stack from "@mui/material/Stack";
import { LightTooltip } from "../../../../../../components/LightTooltip";

interface SpendItemProps {
  item: HierarchicalSpendItem;
  level: number;
  totalValue: number;
  expandedItems: Set<string>;
  onToggleExpand: (itemId: string) => void;
}

export const SpendItem = ({
  item,
  level,
  totalValue,
  expandedItems,
  onToggleExpand,
}: SpendItemProps) => {
  const progress = calculateProgress(item.value, totalValue);
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.has(item.id);

  // Sort children by value (descending)
  const sortedChildren = useMemo(() => {
    if (!item.children) return [];
    return [...item.children].sort((a, b) => b.value - a.value);
  }, [item.children]);

  const handleToggle = () => {
    if (hasChildren) {
      onToggleExpand(item.id);
    }
  };

  return (
    <Box key={item.id}>
      <Box mb={2} ml={level * 2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={0.5}
        >
          <Stack direction="row" alignItems="center" flex={1}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ mr: 0.5, p: 0.5 }}
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box width={30} mr={0.5} /> // Spacer for alignment
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: level === 0 ? 600 : 400,
                cursor: hasChildren ? "pointer" : "default",
              }}
              onClick={handleToggle}
            >
              {item.name}
            </Typography>
          </Stack>
          <Stack direction="column" alignItems="flex-start">
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {getNumberLabel(item.value, { isCurrency: true, compact: true })}
            </Typography>
            <LightTooltip
              title={
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    Value:{" "}
                    {getNumberLabel(item.value, {
                      isCurrency: true,
                      compact: true,
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    Percentage: {progress.toFixed(1)}%
                  </Typography>
                </Box>
              }
              placement="top"
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  width: 190,
                  height: 8,
                  borderRadius: 10,
                  backgroundColor: "#f0f0f0",
                  cursor: "help",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 10,
                    backgroundColor: getColorByLevel(level),
                  },
                }}
              />
            </LightTooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Render children if expanded */}
      {hasChildren && isExpanded && (
        <>
          {sortedChildren.map((child) => (
            <SpendItem
              key={child.id}
              item={child}
              level={level + 1}
              totalValue={totalValue}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </>
      )}
    </Box>
  );
};
